// An organisation's custom apps in the venue Library (spec/13 §9; F-CAT-02): "Custom apps not in the catalogue:
// name, icon, launch type and path, arguments, working directory…". A custom app belongs to the organisation, so
// every venue of it can offer it; this venue adds one to its library like any catalogue title.
//
//   CustomAppsPanel  the Library's "Custom apps" tab: the organisation's apps, each with Edit, Add to this venue
//                    and Archive (`library:manage`).
//   CustomAppDialog  a short stepper (BRAND.md §6, ADR-0027) rather than one long form:
//                    Name and category → How to launch → Detection → Icon → Confirm (and add to this venue).
//                    "How to launch" is one launch recipe — a program, a shortcut, a link or a Store app — with the
//                    path variables a PC resolves listed beside it; "Detection" starts from the program's file name.
//                    Nothing is written until Confirm; the icon is uploaded once the app exists.

import type { AppCategory, CustomApp, LaunchRecipe, LaunchRecipeType, VenueCustomApp } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, Checkbox, Dialog, EmptyState, SelectField, Stepper, TextField, useToast } from '@clavian/ui-web';
import { useMemo, useState, type ReactNode } from 'react';

import { useApi } from '../../api/client';
import { PROBLEM, problemCode } from '../../api/problems';
import { formatIconCap, ICON_MAX_BYTES, iconFileProblem } from '../../hq/catalogue/icon-upload';
import type { PutWithProgress } from '../../hq/catalogue/trailer-upload';
import { UploadDrop } from '../../hq/catalogue/UploadDrop';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon, NavIcon } from '../../shell/nav-icons';
import { CardsSkeleton, Notice, RowActions } from '../../ui';
import { uploadCustomAppIcon, useAddLibraryEntry, useArchiveCustomApp, useCreateCustomApp, useUpdateCustomApp, useVenueCustomApps } from './queries';
import { useProblem } from '../../api/use-problem';

/** The kiosk categories a custom app may suggest (`AppCategory`), in the picker's order. */
export const CUSTOM_APP_CATEGORIES: readonly AppCategory[] = ['apps', 'browsers', 'voice', 'media', 'tools'];

/** The launch types, in the picker's order. */
const LAUNCH_TYPES: readonly LaunchRecipeType[] = ['exe', 'lnk', 'uri', 'store'];

/** The path variables a PC resolves (`LAUNCH_PATH_VARIABLES` in the contract, spec/10 §5.2). */
export const PATH_VARIABLES = ['{ProgramFiles}', '{UserProfile}', '{SteamLibrary}', '{EpicInstall:<AppName>}'] as const;

/** Process names as typed: comma- or newline-separated, blanks dropped. The server normalises the rest. */
export function parseProcessNames(text: string): string[] {
  return text
    .split(/[,\n]/)
    .map((name) => name.trim())
    .filter((name) => name !== '');
}

/** The process name a program path suggests: its file name without `.exe` — `…\arena.exe` → `arena`. */
export function processNameFromPath(path: string): string {
  const file = path.trim().split(/[\\/]/).at(-1) ?? '';
  return /\.exe$/i.test(file) ? file.slice(0, -4) : '';
}

interface LaunchDraft {
  type: LaunchRecipeType;
  path: string;
  args: string;
  workingDirectory: string;
  uri: string;
  aumid: string;
}

interface CustomAppDraft {
  name: string;
  category: AppCategory;
  launch: LaunchDraft;
  processNames: string;
  icon: File | null;
  addToVenue: boolean;
}

function launchDraftOf(recipe: LaunchRecipe | undefined): LaunchDraft {
  return {
    type: recipe?.type ?? 'exe',
    path: recipe?.path ?? '',
    args: recipe?.args ?? '',
    workingDirectory: recipe?.workingDirectory ?? '',
    uri: recipe?.uri ?? '',
    aumid: recipe?.aumid ?? '',
  };
}

function draftOf(app: CustomApp | undefined): CustomAppDraft {
  return {
    name: app?.name ?? '',
    category: app?.suggestedCategory ?? 'apps',
    launch: launchDraftOf(app?.launchRecipes[0]),
    processNames: app === undefined ? '' : app.detection.processNames.join(', '),
    icon: null,
    addToVenue: app === undefined,
  };
}

/** The recipe a launch draft makes, or `null` while its required field is empty. Only the type's own fields are sent. */
export function recipeOf(draft: LaunchDraft): LaunchRecipe | null {
  const text = (value: string) => value.trim();
  switch (draft.type) {
    case 'exe': {
      if (text(draft.path) === '') return null;
      return {
        launcher: 'standalone',
        type: 'exe',
        path: text(draft.path),
        ...(text(draft.args) === '' ? {} : { args: text(draft.args) }),
        ...(text(draft.workingDirectory) === '' ? {} : { workingDirectory: text(draft.workingDirectory) }),
      };
    }
    case 'lnk':
      return text(draft.path) === '' ? null : { launcher: 'standalone', type: 'lnk', path: text(draft.path) };
    case 'uri':
      return text(draft.uri) === '' ? null : { launcher: 'other', type: 'uri', uri: text(draft.uri) };
    case 'store':
      return text(draft.aumid) === '' ? null : { launcher: 'microsoft_store', type: 'store', aumid: text(draft.aumid) };
  }
}

/** Where a recipe points, for a one-line summary: its path, URI or AUMID. */
function targetOf(recipe: LaunchRecipe | undefined): string {
  return recipe?.path ?? recipe?.uri ?? recipe?.aumid ?? '';
}

// -------------------------------------------------------------------------------------------------
// The panel
// -------------------------------------------------------------------------------------------------

export function CustomAppsPanel({
  organizationId,
  venueId,
  canManage,
  onCreate,
  onEdit,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly canManage: boolean;
  readonly onCreate: () => void;
  readonly onEdit: (app: CustomApp) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const apps = useVenueCustomApps(organizationId, venueId);

  if (apps.data === undefined) {
    return apps.isError ? (
      <LoadFailure error={apps.error} message={t('library.customApps.loadFailed')} onRetry={() => void apps.refetch()} />
    ) : (
      <CardsSkeleton label={t('library.customApps.loading')} />
    );
  }
  const rows = apps.data.pages.flatMap((page) => page.data);
  return (
    <>
      {/* What custom apps are, and — once there are some — Add custom app beside them (the page's header keeps Add title). */}
      <div className="console-custom-apps__bar">
        <p className="console-muted console-custom-apps__intro">{t('library.customApps.intro')}</p>
        {canManage && rows.length > 0 ? (
          <Button variant="secondary" icon={<Icon name="plus" />} onClick={onCreate}>
            {t('library.customApps.open')}
          </Button>
        ) : null}
      </div>
      {rows.length === 0 ? (
        <EmptyState
          icon={<NavIcon name="library" />}
          title={t('library.customApps.empty.title')}
          description={t('library.customApps.empty.body')}
          action={canManage ? <Button variant="secondary" onClick={onCreate}>{t('library.customApps.open')}</Button> : undefined}
        />
      ) : (
        <ul className="console-custom-apps" aria-label={t('library.customApps.caption')}>
          {rows.map((row) => (
            <CustomAppRow key={row.customApp.id} row={row} canManage={canManage} organizationId={organizationId} venueId={venueId} onEdit={() => onEdit(row.customApp)} />
          ))}
        </ul>
      )}
      {apps.hasNextPage ? (
        <div className="console-library-more">
          <Button busy={apps.isFetchingNextPage} onClick={() => void apps.fetchNextPage()}>
            {t('library.list.more')}
          </Button>
        </div>
      ) : null}
    </>
  );
}

function CustomAppRow({
  row,
  canManage,
  organizationId,
  venueId,
  onEdit,
}: {
  readonly row: VenueCustomApp;
  readonly canManage: boolean;
  readonly organizationId: string;
  readonly venueId: string;
  readonly onEdit: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const add = useAddLibraryEntry(organizationId, venueId);
  const archive = useArchiveCustomApp(organizationId, venueId);
  const app = row.customApp;
  const recipe = app.launchRecipes[0];
  // Archiving a custom app takes it off every venue in the organisation and cannot be undone here, so the
  // menu's Archive opens a confirm in place rather than firing on one click (design guide §4).
  const [confirming, setConfirming] = useState(false);

  return (
    <li className="console-custom-apps__row">
      <CustomAppIcon app={app} />
      <div className="console-custom-apps__body">
        <p className="console-library-card__name">{app.name}</p>
        <p className="console-library-card__meta">
          <Badge>{t(`library.category.${app.suggestedCategory}`)}</Badge>
          {row.inLibrary ? <Badge tone="ok">{t('library.customApps.inLibrary')}</Badge> : <Badge>{t('library.customApps.notInLibrary')}</Badge>}
        </p>
        {recipe !== undefined ? (
          <p className="console-custom-apps__launch">
            {t(`library.customApps.launchSummary.${recipe.type}`)} · <code>{targetOf(recipe)}</code>
          </p>
        ) : null}
      </div>
      {canManage ? (
        <RowActions
          name={app.name}
          items={[
            { id: 'edit', label: t('common.edit'), onSelect: onEdit },
            ...(row.inLibrary
              ? []
              : [
                  {
                    id: 'add',
                    label: t('library.customApps.addHere'),
                    onSelect: () =>
                      add.mutate(
                        { customAppId: app.id, category: app.suggestedCategory },
                        {
                          onSuccess: () => show({ tone: 'ok', title: t('library.customApps.addedHere', { app: app.name }) }),
                          onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('library.customApps.addHereFailed', { app: app.name }) }) }),
                        },
                      ),
                  },
                ]),
            {
              id: 'archive',
              label: t('library.customApps.archive'),
              tone: 'danger' as const,
              onSelect: () => setConfirming(true),
            },
          ]}
        />
      ) : null}
      {confirming ? (
        <div className="console-custom-apps__confirm" role="group" aria-label={t('library.customApps.archiveLabel', { app: app.name })}>
          <span className="console-subtle">{t('library.customApps.archiveConfirm', { app: app.name })}</span>
          <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
            {t('library.customApps.archiveKeep')}
          </Button>
          <Button
            variant="danger"
            size="sm"
            busy={archive.isPending}
            onClick={() =>
              archive.mutate(
                { customAppId: app.id },
                {
                  onSuccess: () => show({ tone: 'ok', title: t('library.customApps.archived', { app: app.name }) }),
                  onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('library.customApps.archiveFailed', { app: app.name }) }) }),
                  onSettled: () => setConfirming(false),
                },
              )
            }
          >
            {t('library.customApps.archive')}
          </Button>
        </div>
      ) : null}
    </li>
  );
}

/** A custom app's square icon, or its initial. */
export function CustomAppIcon({ app, size = 56 }: { readonly app: Pick<CustomApp, 'name' | 'iconUrl'>; readonly size?: number }): ReactNode {
  return app.iconUrl !== null ? (
    <img className="console-custom-apps__icon" src={app.iconUrl} alt="" loading="lazy" width={size} height={size} />
  ) : (
    <span className="console-custom-apps__icon console-custom-apps__icon--placeholder" aria-hidden="true">
      {app.name.slice(0, 1).toUpperCase()}
    </span>
  );
}

// -------------------------------------------------------------------------------------------------
// The dialog
// -------------------------------------------------------------------------------------------------

export function CustomAppDialog({
  organizationId,
  venueId,
  app,
  open,
  onOpenChange,
  put,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly app?: CustomApp | undefined;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The signed PUT, injected by a test; the browser's XHR otherwise. */
  readonly put?: PutWithProgress;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      dirty="auto"
      title={app === undefined ? t('library.customApps.sheet.createTitle') : t('library.customApps.sheet.editTitle')}
      description={app?.name}
    >
      {open ? <CustomAppFlow organizationId={organizationId} venueId={venueId} app={app} put={put} onDone={() => onOpenChange(false)} /> : null}
    </Dialog>
  );
}

type StepId = 'name' | 'launch' | 'detection' | 'icon' | 'confirm';
const STEPS: readonly StepId[] = ['name', 'launch', 'detection', 'icon', 'confirm'];

function CustomAppFlow({
  organizationId,
  venueId,
  app,
  put,
  onDone,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly app: CustomApp | undefined;
  readonly put: PutWithProgress | undefined;
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const api = useApi();
  const create = useCreateCustomApp(organizationId, venueId);
  const update = useUpdateCustomApp(organizationId, venueId);
  const addEntry = useAddLibraryEntry(organizationId, venueId);
  const creating = app === undefined;
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<CustomAppDraft>(() => draftOf(app));
  const [error, setError] = useState<string | undefined>(undefined);
  const [iconError, setIconError] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const current = STEPS[step] ?? 'name';
  const cap = formatIconCap(ICON_MAX_BYTES);
  const categories = useMemo(() => CUSTOM_APP_CATEGORIES.map((category) => ({ value: category, label: t(`library.category.${category}`) })), [t]);
  const types = useMemo(() => LAUNCH_TYPES.map((type) => ({ value: type, label: t(`library.customApps.sheet.types.${type}`) })), [t]);
  const setLaunch = (patch: Partial<LaunchDraft>) => setDraft({ ...draft, launch: { ...draft.launch, ...patch } });
  const recipe = recipeOf(draft.launch);

  const next = () => {
    if (current === 'name' && draft.name.trim() === '') {
      setError(t('library.customApps.sheet.nameRequired'));
      return;
    }
    if (current === 'launch') {
      if (recipe === null) {
        setError(t('library.customApps.sheet.launchRequired'));
        return;
      }
      // Detection starts from the program's file name, when nothing is written there yet.
      if (draft.processNames.trim() === '' && (draft.launch.type === 'exe' || draft.launch.type === 'lnk')) {
        const suggested = processNameFromPath(draft.launch.path);
        if (suggested !== '') setDraft({ ...draft, processNames: suggested });
      }
    }
    setError(undefined);
    setStep((index) => Math.min(index + 1, STEPS.length - 1));
  };

  const submit = async () => {
    if (recipe === null) {
      setStep(STEPS.indexOf('launch'));
      setError(t('library.customApps.sheet.launchRequired'));
      return;
    }
    const detection = { processNames: parseProcessNames(draft.processNames) };
    setError(undefined);
    setSaving(true);
    try {
      const saved =
        app === undefined
          ? await create.mutateAsync({ name: draft.name.trim(), suggestedCategory: draft.category, launchRecipes: [recipe], detection })
          : await update.mutateAsync({
              customAppId: app.id,
              body: { name: draft.name.trim(), suggestedCategory: draft.category, launchRecipes: [recipe, ...app.launchRecipes.slice(1)], detection },
            });
      let iconFailed = false;
      if (draft.icon !== null) {
        try {
          await uploadCustomAppIcon(api, organizationId, saved.id, draft.icon, put);
        } catch {
          iconFailed = true;
        }
      }
      let added = false;
      if (creating && draft.addToVenue) {
        await addEntry.mutateAsync({ customAppId: saved.id, category: saved.suggestedCategory });
        added = true;
      }
      if (iconFailed) show({ tone: 'warn', title: t('library.customApps.sheet.iconFailed', { app: saved.name }) });
      else if (!creating) show({ tone: 'ok', title: t('library.customApps.sheet.saved', { app: saved.name }) });
      else show({ tone: 'ok', title: added ? t('library.customApps.sheet.createdAndAdded', { app: saved.name }) : t('library.customApps.sheet.created', { app: saved.name }) });
      onDone();
    } catch (err) {
      setError(problemCode(err) === PROBLEM.conflict ? t('library.customApps.sheet.nameTaken') : problem.text(err, { fallback: t('library.customApps.sheet.failed') }));
    } finally {
      setSaving(false);
    }
  };

  const label = (id: StepId) =>
    t(
      id === 'name'
        ? 'library.customApps.sheet.stepName'
        : id === 'launch'
          ? 'library.customApps.sheet.stepLaunch'
          : id === 'detection'
            ? 'library.customApps.sheet.stepDetection'
            : id === 'icon'
              ? 'library.customApps.sheet.stepIcon'
              : 'library.customApps.sheet.stepConfirm',
    );

  return (
    <div className="console-stepper-flow">
      <Stepper current={step} onStepSelect={(index) => index < step && setStep(index)} steps={STEPS.map((id) => ({ id, label: label(id) }))} />
      <div className="console-form">
        {error !== undefined ? (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        ) : null}

        {current === 'name' ? (
          <>
            <TextField
              label={t('library.customApps.sheet.name')}
              description={t('library.customApps.sheet.nameHint')}
              required
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />
            <SelectField
              label={t('library.customApps.sheet.category')}
              description={t('library.customApps.sheet.categoryHint')}
              options={categories}
              value={draft.category}
              onValueChange={(value) => setDraft({ ...draft, category: value as AppCategory })}
            />
          </>
        ) : null}

        {current === 'launch' ? (
          <>
            <SelectField label={t('library.customApps.sheet.type')} options={types} value={draft.launch.type} onValueChange={(value) => setLaunch({ type: value as LaunchRecipeType })} />
            {draft.launch.type === 'exe' || draft.launch.type === 'lnk' ? (
              <>
                <p className="console-muted">{t('library.customApps.sheet.launchIntro')}</p>
                <ul className="console-custom-apps__variables" aria-label={t('library.customApps.sheet.launchIntro')}>
                  {PATH_VARIABLES.map((variable) => (
                    <li key={variable}>
                      <code>{variable}</code>
                    </li>
                  ))}
                </ul>
                <TextField
                  label={t('library.customApps.sheet.path')}
                  description={draft.launch.type === 'exe' ? t('library.customApps.sheet.pathHintExe') : t('library.customApps.sheet.pathHintLnk')}
                  required
                  value={draft.launch.path}
                  onChange={(event) => setLaunch({ path: event.target.value })}
                />
              </>
            ) : null}
            {draft.launch.type === 'exe' ? (
              <>
                <TextField label={t('library.customApps.sheet.args')} showOptional value={draft.launch.args} onChange={(event) => setLaunch({ args: event.target.value })} />
                <TextField
                  label={t('library.customApps.sheet.workingDirectory')}
                  showOptional
                  value={draft.launch.workingDirectory}
                  onChange={(event) => setLaunch({ workingDirectory: event.target.value })}
                />
              </>
            ) : null}
            {draft.launch.type === 'uri' ? (
              <TextField label={t('library.customApps.sheet.uri')} description={t('library.customApps.sheet.uriHint')} required value={draft.launch.uri} onChange={(event) => setLaunch({ uri: event.target.value })} />
            ) : null}
            {draft.launch.type === 'store' ? (
              <TextField label={t('library.customApps.sheet.aumid')} description={t('library.customApps.sheet.aumidHint')} required value={draft.launch.aumid} onChange={(event) => setLaunch({ aumid: event.target.value })} />
            ) : null}
          </>
        ) : null}

        {current === 'detection' ? (
          <>
            <p className="console-muted">{t('library.customApps.sheet.detectionIntro')}</p>
            <TextField
              label={t('library.customApps.sheet.processNames')}
              description={t('library.customApps.sheet.processNamesHint')}
              showOptional
              value={draft.processNames}
              onChange={(event) => setDraft({ ...draft, processNames: event.target.value })}
            />
          </>
        ) : null}

        {current === 'icon' ? (
          <>
            <p className="console-muted">{t('library.customApps.sheet.iconIntro', { size: cap })}</p>
            <UploadDrop
              label={t('library.customApps.sheet.iconFile')}
              title={t('library.customApps.sheet.iconDrop')}
              hint={t('library.customApps.sheet.iconDropHint', { size: cap })}
              accept="image/png,image/webp"
              fileName={draft.icon?.name ?? null}
              onFile={(file) => {
                const problem = file === null ? undefined : iconFileProblem(file);
                if (problem === 'wrongType') setIconError(t('library.customApps.sheet.iconWrongType'));
                else if (problem === 'tooLarge') setIconError(t('library.customApps.sheet.iconTooLarge', { size: cap }));
                else setIconError(undefined);
                setDraft({ ...draft, icon: problem === undefined ? file : null });
              }}
            />
            {iconError !== undefined ? (
              <Notice tone="danger" role="alert">
                {iconError}
              </Notice>
            ) : (
              <p className="console-muted" role="status">
                {draft.icon !== null
                  ? t('library.customApps.sheet.iconChosen', { name: draft.icon.name })
                  : app !== undefined && app.iconUrl !== null
                    ? t('library.customApps.sheet.iconKeep')
                    : t('library.customApps.sheet.iconNone')}
              </p>
            )}
          </>
        ) : null}

        {current === 'confirm' ? (
          <>
            <p className="console-muted">{t('library.customApps.sheet.confirmIntro')}</p>
            <dl className="console-custom-apps__summary">
              <dt>{t('library.customApps.sheet.name')}</dt>
              <dd>{draft.name.trim()}</dd>
              <dt>{t('library.customApps.sheet.category')}</dt>
              <dd>{t(`library.category.${draft.category}`)}</dd>
              <dt>{t('library.customApps.sheet.stepLaunch')}</dt>
              <dd>
                {t(`library.customApps.launchSummary.${draft.launch.type}`)} · <code>{targetOf(recipe ?? undefined)}</code>
              </dd>
              <dt>{t('library.customApps.sheet.processNames')}</dt>
              <dd>{parseProcessNames(draft.processNames).length === 0 ? t('library.customApps.sheet.detectionNone') : parseProcessNames(draft.processNames).join(', ')}</dd>
              <dt>{t('library.customApps.sheet.stepIcon')}</dt>
              <dd>{draft.icon === null ? (app !== undefined && app.iconUrl !== null ? t('library.customApps.sheet.iconKeep') : t('library.customApps.sheet.iconNone')) : draft.icon.name}</dd>
            </dl>
            {creating ? (
              <div className="console-library-toggle">
                <Checkbox label={t('library.customApps.sheet.addToVenue')} checked={draft.addToVenue} onCheckedChange={(checked) => setDraft({ ...draft, addToVenue: checked === true })} />
                <p className="console-muted">{t('library.customApps.sheet.addToVenueHint')}</p>
              </div>
            ) : null}
          </>
        ) : null}

        <div className="console-form__actions">
          {step > 0 ? (
            <Button type="button" variant="ghost" onClick={() => setStep(step - 1)}>
              {t('library.customApps.sheet.back')}
            </Button>
          ) : (
            <Button type="button" variant="ghost" onClick={onDone}>
              {t('library.customApps.sheet.cancel')}
            </Button>
          )}
          <span style={{ flex: 1 }} />
          {current === 'confirm' ? (
            <Button type="button" variant="primary" busy={saving} onClick={() => void submit()}>
              {creating ? t('library.customApps.sheet.create') : t('library.customApps.sheet.save')}
            </Button>
          ) : (
            <Button type="button" variant="primary" onClick={next}>
              {t('library.customApps.sheet.next')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
